// Shared helpers for the Deposits page and the Expenses page.
//
// Both pages write an entry to its own collection (deposits, subcontractorPay,
// employeePay, monthlyBills, bankDeductions) and mirror it into the shared
// "transactions" ledger that the Financials Dashboard and Accounting Exports
// read. The txId stored on each entry links the two so edits and deletes stay
// in sync. These builders are unchanged from the original Deposits.js.

import {
  collection,
  addDoc,
  getDocs,
  deleteDoc,
  doc,
  updateDoc,
  query,
  where,
  serverTimestamp,
} from "firebase/firestore";
import { db } from "../firebaseConfig";

export const todayStr = () => new Date().toISOString().split("T")[0];

export const parseAmount = (val) => {
  const num = parseFloat(val);
  return isNaN(num) ? 0 : num;
};

export const formatMoney = (num) => {
  const n = Number(num) || 0;
  const body = Math.abs(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return `${n < 0 ? "−" : ""}$${body}`;
};

/** "2026-09-27" -> "09/27/2026" (no time zone shifting). */
export const formatDateMDY = (value) => {
  if (!value || typeof value !== "string") return value ? String(value) : "—";
  const m = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? `${m[2]}/${m[3]}/${m[1]}` : value;
};

export const sortByDateDesc = (list) =>
  [...list].sort((a, b) => new Date(b.date || "1900-01-01") - new Date(a.date || "1900-01-01"));

/* ---------------- transactions mirror ---------------- */

export const upsertTransaction = async ({ txId, payload }) => {
  if (txId) {
    await updateDoc(doc(db, "transactions", txId), payload);
    return txId;
  }
  const ref = await addDoc(collection(db, "transactions"), payload);
  return ref.id;
};

/**
 * Remove the ledger row for an entry. Uses the stored txId first; if the entry
 * never got one, falls back to looking the row up by its source so nothing is
 * left behind to be counted twice.
 */
export const deleteLinkedTransaction = async ({ txId, sourceCollection, sourceId }) => {
  try {
    if (txId) {
      await deleteDoc(doc(db, "transactions", txId));
      return;
    }
    if (!sourceCollection || !sourceId) return;
    const snap = await getDocs(
      query(
        collection(db, "transactions"),
        where("sourceCollection", "==", sourceCollection),
        where("sourceId", "==", sourceId)
      )
    );
    await Promise.all(snap.docs.map((d) => deleteDoc(d.ref)));
  } catch (e) {
    console.warn("Failed deleting linked transaction:", txId || sourceId, e);
  }
};

export const buildTxFromDeposit = (depositDoc, jobId = null) => ({
  date: depositDoc.date || todayStr(),
  type: "income",
  amount: parseAmount(depositDoc.amount),
  category: depositDoc.paymentType || "Deposit",
  vendor: depositDoc.company || "",
  memo: `Check# ${depositDoc.checkNumber || ""} | WO ${depositDoc.invoiceNumber || ""} | ${depositDoc.description || ""}`.trim(),
  jobId,
  method: "check",
  sourceCollection: "deposits",
  sourceId: depositDoc.id || null,
  updatedAt: serverTimestamp(),
});

export const buildTxFromEmpPay = (empDoc) => ({
  date: empDoc.date || todayStr(),
  type: "expense",
  amount: parseAmount(empDoc.amount),
  category: "Labor",
  vendor: empDoc.employeeName || "Employee",
  memo: `${empDoc.payType || "Regular"} | ${empDoc.description || ""}`.trim(),
  jobId: null,
  method: "manual",
  sourceCollection: "employeePay",
  sourceId: empDoc.id || null,
  updatedAt: serverTimestamp(),
});

export const buildTxFromSubPay = (subDoc) => ({
  date: subDoc.date || todayStr(),
  type: "expense",
  amount: parseAmount(subDoc.amount),
  category: "Subcontractor",
  vendor: subDoc.property || "Sub",
  memo: `${subDoc.category || ""} | ${subDoc.description || ""}`.trim(),
  jobId: null,
  method: "manual",
  sourceCollection: "subcontractorPay",
  sourceId: subDoc.id || null,
  updatedAt: serverTimestamp(),
});

export const buildTxFromBill = (billDoc) => ({
  date: billDoc.date || todayStr(),
  type: "expense",
  amount: parseAmount(billDoc.amount),
  category: "Overhead",
  vendor: billDoc.vendor || "",
  memo: `${billDoc.billType || ""} | ${billDoc.description || ""}`.trim(),
  jobId: null,
  method: "manual",
  sourceCollection: "monthlyBills",
  sourceId: billDoc.id || null,
  updatedAt: serverTimestamp(),
});

export const buildTxFromBankDed = (bankDoc) => ({
  date: bankDoc.date || todayStr(),
  type: "expense",
  amount: parseAmount(bankDoc.amount),
  category: "Bank Fees",
  vendor: bankDoc.bankName || "",
  memo: `${bankDoc.deductionType || ""} | ${bankDoc.statementMonth || ""} | ${bankDoc.statementCategory || ""} | ${bankDoc.description || ""}`.trim(),
  jobId: null,
  method: "bank",
  sourceCollection: "bankDeductions",
  sourceId: bankDoc.id || null,
  updatedAt: serverTimestamp(),
});

/**
 * Deposits created by Bank Statement Entries already have their own row in
 * transactions (sourceCollection "bankStatementEntries"). Mirroring them again
 * from Deposits would count that money twice.
 */
export const depositHasOwnLedgerRow = (deposit) =>
  deposit?.sourceCollection === "bankStatementEntries" || deposit?.source === "bankStatementEntries";

/* ---------------- bulk upload templates ---------------- */

export const BULK_TEMPLATES = {
  deposits: {
    label: "Checks / Deposits",
    filename: "Bulk_Deposits_Template.xlsx",
    headers: ["Property", "Amount", "Check #", "Payment Type", "Work Order #", "Invoice #2", "Description", "Company", "Date"],
  },
  subPay: {
    label: "Subcontractor Pay",
    filename: "Bulk_Subcontractor_Pay_Template.xlsx",
    headers: ["Property", "Amount", "Category", "Description", "Effect", "Date"],
  },
  empPay: {
    label: "Employee Pay",
    filename: "Bulk_Employee_Pay_Template.xlsx",
    headers: ["Employee", "Property", "Amount", "Pay Type", "Description", "Effect", "Date"],
  },
  bills: {
    label: "Monthly Bills",
    filename: "Bulk_Monthly_Bills_Template.xlsx",
    headers: ["Vendor", "Amount", "Bill Type", "Description", "Effect", "Date"],
  },
  bankDed: {
    label: "Bank Deductions",
    filename: "Bulk_Bank_Deductions_Template.xlsx",
    headers: ["Bank", "Amount", "Type", "Statement Month", "Category", "Description", "Effect", "Date"],
  },
};
